import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Table from 'src/components/VehicleTrackingDetails/Tables/Table';
import UploadPODComponent from 'src/components/VehicleTrackingLocationShare/UploadPODComponent';
import VehicleTrackingLocationSharePODUploadModal from 'src/components/VehicleTrackingLocationShare/VehicleTrackingLocationSharePODUploadModal';
import getColorCode from 'src/utils/GetThemeColor';
import {
  createPODAttachmentsForVehicleTracking,
  getPODAttachmentsForVehicleTracking,
  resetPODAttachmentsForVehicleTracking,
} from 'src/redux/Actions/Vehicle';
import { ReactComponent as EyeIcon } from 'src/Images/icons/common/eye.svg';
import { ReactComponent as PlusIcon } from 'src/Images/icons/common/plus.svg';
import 'src/style/invoice.css';

export default function VehicleTrackingLocationSharePodTab({
  vehicleTrackingNumber,
  themeColor,
}) {
  const dispatch = useDispatch();
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPODUploadModal, setShowPODUploadModal] = useState(false);

  const podAttachmentsData = useSelector(
    (state) => state.VehicleReducer.podAttachmentsForVehicleTracking,
  );
  const podAttachments = podAttachmentsData.attachments;

  const fetchPODAttachments = useCallback(() => {
    setLoading(true);
    return dispatch(getPODAttachmentsForVehicleTracking(vehicleTrackingNumber))
      .catch(() => {
        setUploadMessage('Unable to load POD attachments.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [dispatch, vehicleTrackingNumber]);

  useEffect(() => {
    fetchPODAttachments();

    return () => {
      dispatch(resetPODAttachmentsForVehicleTracking());
    };
  }, [dispatch, fetchPODAttachments]);

  const handleFileUpload = async (files) => {
    try {
      setUploading(true);
      setUploadMessage('');

      const formData = new FormData();
      files.forEach((file) => formData.append('file', file));
      formData.append('vehicle_tracking_number', vehicleTrackingNumber);

      await dispatch(createPODAttachmentsForVehicleTracking(formData));
      setUploadMessage(
        `${files.length > 1 ? `${files.length} POD files` : 'POD'} uploaded successfully.`,
      );
      await fetchPODAttachments();
    } catch (error) {
      setUploadMessage(
        error.response?.data?.error ||
          error.message ||
          'Failed to upload POD. Please try again.',
      );
    } finally {
      setUploading(false);
    }
  };

  const podTableHeaders = [
    { label: 'File', value: 'filename' },
    { label: 'Type', value: 'file_type_display' },
    { label: 'Actions', value: 'actions' },
  ];

  const renderTableContent = () => (
    <tbody>
      {podAttachments.map((podAttachment) => (
        <tr key={podAttachment.attachment_id}>
          <td className="table-data">
            <div className="div-flex-column rg-5">
              <span
                className="text-sm text-bold"
                title={podAttachment.filename}
              >
                {podAttachment.filename}
              </span>
              <span className="text-xs text-grey">
                {new Date(podAttachment.created).toLocaleDateString()}
              </span>
            </div>
          </td>
          <td className="table-data">
            <span className="text-sm">{podAttachment.file_type_display}</span>
          </td>
          <td className="table-data">
            <button
              className="btn btn-sm btn-light"
              onClick={() =>
                window.open(podAttachment.attachment_url, '_blank')
              }
              title="Preview"
              aria-label="Preview POD"
              disabled={!podAttachment.attachment_url}
            >
              <EyeIcon className="icon-size-lg" />
            </button>
          </td>
        </tr>
      ))}
    </tbody>
  );

  const hasPODAttachments = podAttachments.length > 0;

  return (
    <div className="invoice-tab">
      {uploadMessage && (
        <div
          className={`alert ${
            uploadMessage.includes('uploaded') ||
            uploadMessage.includes('success')
              ? 'alert-success'
              : 'alert-danger'
          } py-2 mb-3`}
          role="alert"
        >
          {uploadMessage}
        </div>
      )}

      {loading && !hasPODAttachments ? (
        <div className="div-flex-column div-align-center rg-10 py-5">
          <div className="spinner-border" role="status" />
          <span className="text-sm">Loading POD...</span>
        </div>
      ) : !hasPODAttachments ? (
        <div className="invoice-empty-state-container div-flex-center div-align-center rg-10">
          <div className="div-flex-column div-align-center rg-5">
            <span className="text-lg text-semi-bold">No POD uploaded yet!</span>
            <span className="text-grey text-sm">
              Take a Photo or Upload a POD image.
            </span>
          </div>
          <UploadPODComponent
            onFileUpload={handleFileUpload}
            uploading={uploading}
            themeColor={themeColor}
          />
        </div>
      ) : (
        <div>
          <div className="div-flex-row div-flex-end div-align-center section-title">
            <button
              className={`vehicle-tracking-detail-add-vehcile-data-btn theme-text-${themeColor} theme-border-color-${themeColor} div-align-center`}
              onClick={() => setShowPODUploadModal(true)}
            >
              <PlusIcon color={getColorCode(themeColor)} />
              <span className="text-md text-semi-bold left-padding">
                Upload POD
              </span>
            </button>
          </div>

          <div
            className={`table-fixed-container table-scrollable-container theme-scrollable-container-${themeColor}`}
          >
            <Table
              headers={podTableHeaders}
              minCellWidth={100}
              tableContent={renderTableContent()}
              stickyHeader
            />
          </div>
        </div>
      )}
      <VehicleTrackingLocationSharePODUploadModal
        show={showPODUploadModal}
        onHide={() => setShowPODUploadModal(false)}
        vehicleTrackingNumber={vehicleTrackingNumber}
        themeColor={themeColor}
        onUploaded={fetchPODAttachments}
      />
    </div>
  );
}
